import { createApi } from '@reduxjs/toolkit/query/react';
import {
  GetOrganizerCreativeByIdResponse,
  GetOrganizerCreativesParams,
  GetOrganizerCreativesResponse,
  PatchOrganizerCreativeStatusByIdResponse,
} from './models';
import { getQueryStringFromParams } from 'lib.shared/src/utils';
import { ModerationFiltersKeys } from '../landings/models';
import { API_EVENT_TAGS } from 'lib.store/src/tags';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';

export const organizerCreativesModeration = createApi({
  reducerPath: 'organizer_creatives_moderation',
  baseQuery: baseQueryWithReauth,
  tagTypes: [API_EVENT_TAGS.ORGANIZER_CREATIVES],
  endpoints: (build) => ({
    getOrganizerCreatives: build.query<GetOrganizerCreativesResponse, GetOrganizerCreativesParams>({
      query: ({ filters, page }) => {
        const statusesQueryString = getQueryStringFromParams(
          filters?.statuses,
          ModerationFiltersKeys.statuses,
        );
        const partnersQueryString = getQueryStringFromParams(
          filters?.partners,
          ModerationFiltersKeys.partners,
        );
        const eventsQueryString = getQueryStringFromParams(
          filters?.events,
          ModerationFiltersKeys.events,
        );
        const pageQueryString = page ? `page=${page}` : '';
        const queryString = [
          statusesQueryString,
          partnersQueryString,
          eventsQueryString,
          pageQueryString,
        ]
          .filter(Boolean)
          .join('&');
        const params = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          'filters[search]': filters?.search || undefined,
        };
        return {
          url: `/organizer/creatives${queryString.length ? '?' + queryString : ''}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
          params,
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_CREATIVES],
    }),
    patchOrganizerCreativeStatusById: build.mutation<
      PatchOrganizerCreativeStatusByIdResponse,
      {
        campaignAd: number;
        status: number;
        transition: number;
        reason?: number;
        comment?: string;
      }
    >({
      query: ({ campaignAd, status, transition, reason, comment }) => {
        const body = {
          transition,
          ...(reason ? { reason } : {}),
          ...(comment ? { comment } : {}),
        };
        return {
          url: `/organizer/creatives/${campaignAd}/status/${status}`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body,
        };
      },
    }),
    getOrganizerCreativeById: build.query<GetOrganizerCreativeByIdResponse, { creativeId: number }>(
      {
        query: ({ creativeId }) => ({
          url: `/organizer/creatives/${creativeId}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        }),
      },
    ),
  }),
});

export const {
  useGetOrganizerCreativesQuery,
  usePatchOrganizerCreativeStatusByIdMutation,
  useLazyGetOrganizerCreativeByIdQuery,
} = organizerCreativesModeration;
